import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL, fetchFile } from '@ffmpeg/util';

let ffmpegInstance = null;

export const exportToMP4 = async (audioFile, imageFile, onProgress) => {
  try {
    if (!ffmpegInstance) {
      onProgress({ phase: 'loading', progress: 0, text: 'Loading video engine (WASM)...' });
      
      const initFFmpeg = () => {
        const instance = new FFmpeg();
        instance.on('log', ({ message }) => {
          console.log('[FFmpeg]', message);
        });
        instance.on('progress', ({ progress }) => {
          const percent = Math.max(0, Math.min(100, Math.round(progress * 100)));
          onProgress({ phase: 'rendering', progress: percent, text: `Rendering MP4... ${percent}%` });
        });
        return instance;
      };

      ffmpegInstance = initFFmpeg();
      
      // Am schimbat pe jsdelivr care oferă suport CORS mai bun și stabilitate
      const baseURL = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd';
      
      try {
        console.log("Attempting to load FFmpeg via Blob URLs...");
        // Metoda principală: Încearcă încărcarea prin Blob URLs (recomandată)
        await ffmpegInstance.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
      } catch (blobError) {
        console.warn("Blob URL load failed. Retrying with direct CDN URLs...", blobError);
        
        // Fallback: Dacă browserul blochează scripturile blob: în Web Worker, folosim URL-uri directe
        onProgress({ phase: 'loading', progress: 0, text: 'Retrying engine load (Fallback)...' });
        
        ffmpegInstance = initFFmpeg(); // Resetăm instanța
        await ffmpegInstance.load({
          coreURL: `${baseURL}/ffmpeg-core.js`,
          wasmURL: `${baseURL}/ffmpeg-core.wasm`,
        });
      }
    }

    onProgress({ phase: 'preparing', progress: 0, text: 'Preparing files for render...' });
    
    // Extragem extensiile corecte
    const audioExt = audioFile.name.split('.').pop().toLowerCase() || 'wav';
    const imageExt = imageFile.name.split('.').pop().toLowerCase() || 'png';
    
    const audioName = `input_audio.${audioExt}`;
    const imageName = `input_image.${imageExt}`;

    await ffmpegInstance.writeFile(audioName, await fetchFile(audioFile));
    await ffmpegInstance.writeFile(imageName, await fetchFile(imageFile));

    onProgress({ phase: 'rendering', progress: 0, text: 'Starting video encoding...' });

    // Rulăm encodarea video
    const execCode = await ffmpegInstance.exec([
      '-loop', '1',
      '-framerate', '1', 
      '-i', imageName,
      '-i', audioName,
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-tune', 'stillimage',
      '-vf', 'scale=1920:1080:force_original_aspect_ratio=decrease,pad=1920:1080:(ow-iw)/2:(oh-ih)/2,format=yuv420p',
      '-c:a', 'aac',
      '-b:a', '192k',
      '-shortest',
      'output.mp4'
    ]);

    if (execCode !== 0) {
      throw new Error("Video encoding process failed. The image format might be unsupported.");
    }

    onProgress({ phase: 'finalizing', progress: 100, text: 'Finalizing MP4 file...' });

    const data = await ffmpegInstance.readFile('output.mp4');
    const blob = new Blob([data.buffer], { type: 'video/mp4' });
    
    // Curățăm memoria virtuală
    await ffmpegInstance.deleteFile(audioName);
    await ffmpegInstance.deleteFile(imageName);
    await ffmpegInstance.deleteFile('output.mp4');

    return blob;

  } catch (error) {
    console.error("Video Export Error:", error);
    ffmpegInstance = null; // Resetăm instanța pentru a permite o nouă încercare curată
    const errorMsg = error instanceof Error ? error.message : String(error);
    throw new Error(`Engine Error: ${errorMsg}`);
  }
};